import type { SpacesConfig, SpacesConfigOptions, SpacesConfigLabels, SpacesPortfolioConfig, PortfolioProperty, CustomFilterConfig } from '@/types/api'
import type { ViewMode, CardType } from '@/utils/urlState'
import { MOCK_DISCLAIMER, MOCK_DISCLAIMER_LINK } from '@/data/mockData'
import { decodeEntitiesDeep } from '@/utils/decodeEntities'

const DEFAULT_OPTIONS: SpacesConfigOptions = {
	hideStartingAt: false,
	hidePa: false,
	hidePriceFilter: false,
	hideDateFilter: false,
	displayUnavailableUnits: false,
	displayUnavailablePlans: false,
	hideAffordablePlans: false,
	displayAffordableUnits: false,
	hideAffordableUnitsPrice: false,
	hideAffordableUnitsDate: false,
	applyInterstitial: false,
	enableSightmapCalculator: false,
	hideApplyCta: false,
	pricingDisplayMode: 'base',
	sameTabDetail: false,
	consolidatedAmenities: false,
	maxUnits: -1,
	hideUnitLabel: false,
	ignorePastDatesForSorting: false,
	useRevManagement: false,
	flatPricing: false,
	defaultSort: 'available_on',
	defaultView: 'default',
	objectTabs: ['units', 'plans'],
	applyProvider: 'None',
	displayAreaRange: false,
	hideUnavailableTag: false,
	contactLink: null,
	outboundLink: null
}

const DEFAULT_LABELS: SpacesConfigLabels = {
	unitLabel: 'Unit',
	pluralUnitLabel: 'Units',
	planTabLabel: 'Plans',
	unitTabLabel: 'Units',
	mapTabLabel: 'Map',
	disclaimer: '',
	disclaimerTop: '',
	disclaimerLink: null,
	noResultsHeadline: 'No options match your filters.',
	noResultsBody: 'Try changing or clearing your filters to see results.',
	noResultsButton: 'Clear Filters',
	planPricingPreText: 'Starting at',
	planPricingPostText: '/mo*',
	unitPricingPreText: 'Total Monthly Leasing Price',
	unitPricingPostText: '/mo*',
	applyInterstitialContent: ''
}

let activeAssetConfig: SpacesConfig | null = null

/**
 * Override the page config. Portfolio unit detail passes the full property config;
 * community view passes getCommunityConfig(), whose options and labels stay market-wide.
 * Pass null to restore.
 */
export function setActiveAssetConfig (config: SpacesConfig | null): void {
	activeAssetConfig = config
}

// Decoded in place (not copied) so runtime mutations of window.SPACES.config stay visible.
const decodedConfigs = new WeakSet<SpacesConfig>()

export function getConfig (): SpacesConfig {
	return activeAssetConfig ?? getPageConfig()
}

/** The config emitted by the shortcode, ignoring any active asset override. */
export function getPageConfig (): SpacesConfig {
	const live = window.SPACES?.config
	if (live) {
		if (!decodedConfigs.has(live)) {
			decodeEntitiesDeep(live)
			decodedConfigs.add(live)
		}
		return live
	}

	return {
		assetId: 0,
		engrainAssetId: '',
		sightmapEmbedUrl: '',
		debugMode: false,
		experimentalFeatures: [],
		cdnEnabled: false,
		restUrl: '/wp-json/spaces/v1/',
		filters: {
			bedrooms: [{ value: '0', label: 'Studio' }, { value: '1', label: 'One Bedroom' }, { value: '2', label: 'Two Bedroom' }, { value: 'custom:2bed.penthouse', label: '2 Bed Penthouse', cssClasses: ['2bed', 'penthouse'] }],
			price: { min: 800, max: 3000 },
			maxDaysOut: 365,
			customFilters: [
				{
					name: 'Features',
					slug: 'features',
					type: 'checkbox',
					core: false,
					options: [
						{ label: 'In-Home Washer & Dryer', value: 'washer-dryer' },
						{ label: 'Stainless Steel Appliances', value: 'stainless' },
						{ label: 'Quartz-Style Countertops', value: 'quartz' },
						{ label: 'Wrap-Around Balcony', value: 'balcony' },
						{ label: 'Ceiling Fan', value: 'ceiling-fan' },
						{ label: 'Gas Fireplace', value: 'fireplace' },
						{ label: 'Smart Thermostat', value: 'smart-thermostat' }
					]
				},
				{
					name: 'Views',
					slug: 'views',
					type: 'select',
					core: false,
					options: [
						{ label: 'Pet Park View', value: 'pet-park' },
						{ label: 'Cabana View', value: 'cabana' },
						{ label: 'Courtyard View', value: 'courtyard' },
						{ label: 'City View', value: 'city' },
						{ label: 'Mountain View', value: 'mountain' },
						{ label: 'Pool View', value: 'pool' }
					]
				}
			]
		},
		options: DEFAULT_OPTIONS,
		labels: {
			...DEFAULT_LABELS,
			disclaimer: MOCK_DISCLAIMER,
			disclaimerTop: MOCK_DISCLAIMER,
			disclaimerLink: MOCK_DISCLAIMER_LINK
		}
	}
}

export function getOptions (): SpacesConfigOptions {
	return getConfig().options
}

export function getLabels (): SpacesConfigLabels {
	return getConfig().labels
}

export const PRICE_STEP = 50

// Matches the PHP default in SPACES\Asset::build_filter_data().
const DEFAULT_PRICE_RANGE = { min: 500, max: 3000 }

/** Price filter bounds from the asset's available plans, widened to whole PRICE_STEP increments. */
export function getPriceBounds (): { min: number; max: number } {
	const price = getConfig().filters?.price
	const valid = price != null && Number.isFinite(price.min) && Number.isFinite(price.max) && price.max > 0
	const { min, max } = valid ? { min: Math.max(0, price.min), max: price.max } : DEFAULT_PRICE_RANGE
	const lower = Math.floor(min / PRICE_STEP) * PRICE_STEP
	const upper = Math.ceil(max / PRICE_STEP) * PRICE_STEP
	return { min: lower, max: upper > lower ? upper : lower + PRICE_STEP }
}

const DEFAULT_MAX_DAYS_OUT = 90

// Mirrors PHP build_filter_data(): int-cast, non-positive falls back to the default.
export function getMaxDaysOut (): number {
	const n = Math.floor(Number(getConfig().filters?.maxDaysOut))
	return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_DAYS_OUT
}

export function isPortfolio (): boolean {
	return getPageConfig().mode === 'portfolio'
}

/** Active CMS custom filters for the page (the PHP config omits inactive ones). */
export function getCustomFilterConfigs (): CustomFilterConfig[] {
	return getPageConfig().filters.customFilters ?? []
}

/** Which core filters the CMS allows. Hide P&A also hides price and date, as in Spaces 2. */
export function getFilterVisibility (): { price: boolean; date: boolean } {
	const { hidePa, hidePriceFilter, hideDateFilter } = getPageConfig().options
	return {
		price: !hidePa && !hidePriceFilter,
		date: !hidePa && !hideDateFilter
	}
}

export function getPortfolioConfig (): SpacesPortfolioConfig | undefined {
	return getPageConfig().portfolio
}

export function getPortfolioProperty (engrainAssetId: string): PortfolioProperty | undefined {
	return getPortfolioConfig()?.properties.find(p => p.engrainAssetId === engrainAssetId)
}

const communityConfigs = new Map<string, SpacesConfig>()

/**
 * Page config retargeted at one market property, so the SightMap embed follows it
 * while options and labels stay market-wide. Data requests target the property explicitly.
 */
export function getCommunityConfig (engrainAssetId: string): SpacesConfig | null {
	const property = getPortfolioProperty(engrainAssetId)
	if (!property) return null
	let config = communityConfigs.get(engrainAssetId)
	if (!config) {
		config = {
			...getPageConfig(),
			assetId: property.assetId,
			engrainAssetId: property.engrainAssetId,
			sightmapEmbedUrl: property.sightmapEmbedUrl
		}
		communityConfigs.set(engrainAssetId, config)
	}
	return config
}

/** API sort keys accepted by the collection endpoints and the sort UI. */
export const VALID_SORT_KEYS = [
	'pr_asc', 'pr_desc',
	'sf_asc', 'sf_desc',
	'dt_asc', 'dt_desc',
	'bd_asc', 'bd_desc',
	'name_asc'
] as const

/**
 * Sort options exposed in the sort UI (desktop ListControls + mobile AllFiltersModal).
 * Single source of truth — mirrors the CMS `spaces_option_default_sort` choices
 * (and the backend VALID_SORT_KEYS) so every configurable default is selectable.
 */
export const SORT_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: 'pr_asc', label: 'Price (Low to High)' },
	{ value: 'pr_desc', label: 'Price (High to Low)' },
	{ value: 'dt_asc', label: 'Available Date (Soonest)' },
	{ value: 'dt_desc', label: 'Available Date (Latest)' },
	{ value: 'sf_asc', label: 'Size (Low to High)' },
	{ value: 'sf_desc', label: 'Size (High to Low)' },
	{ value: 'bd_asc', label: 'Beds (Fewest)' },
	{ value: 'bd_desc', label: 'Beds (Most)' },
	{ value: 'name_asc', label: 'Plan Name' }
]

/** Sorts on data that isn't shown when P&A is hidden (matches Spaces 2's sort control). */
const PA_SORT_PATTERN = /^(pr|dt)_/

/** Plan names aren't comparable across a market's properties, so portfolio mode drops the sort. */
const PORTFOLIO_EXCLUDED_SORTS = new Set(['name_asc'])

/** Sort options available under the current config. */
export function getSortOptions (): { value: string; label: string }[] {
	const hidePa = getPageConfig().options.hidePa
	const portfolio = isPortfolio()
	if (!hidePa && !portfolio) return SORT_OPTIONS
	return SORT_OPTIONS.filter(o =>
		!(hidePa && PA_SORT_PATTERN.test(o.value)) &&
		!(portfolio && PORTFOLIO_EXCLUDED_SORTS.has(o.value))
	)
}

export function isSortAvailable (key: string): boolean {
	return getSortOptions().some(o => o.value === key)
}

// A Map so CMS input like `constructor` can't resolve to an Object.prototype member.
const DEFAULT_SORT_MAP = new Map<string, string>([
	['available_on', 'dt_asc'],
	['price', 'pr_asc'],
	['area', 'sf_asc'],
	['bed_count', 'bd_asc'],
	['plan_name', 'name_asc'],
	['pr_asc', 'pr_asc'],
	['pr_desc', 'pr_desc'],
	['dt_asc', 'dt_asc'],
	['dt_desc', 'dt_desc'],
	['sf_asc', 'sf_asc'],
	['sf_desc', 'sf_desc'],
	['bd_asc', 'bd_asc'],
	['bd_desc', 'bd_desc'],
	['name_asc', 'name_asc']
])

export function resolveDefaultSort (): string {
	const raw = getPageConfig().options.defaultSort
	const resolved = DEFAULT_SORT_MAP.get(raw) ?? 'pr_asc'
	return isSortAvailable(resolved) ? resolved : getSortOptions()[0].value
}

/**
 * Resolves the initial view from CMS config, falling back to the responsive
 * default (desktop split / mobile grid). Split is desktop-only, so a configured
 * split default is downgraded to grid on mobile; map remains valid on mobile.
 */
export function resolveDefaultView (isDesktop: boolean): ViewMode {
	const configured = getPageConfig().options.defaultView
	const view: ViewMode = !configured || configured === 'default'
		? (isDesktop ? 'split' : 'grid')
		: configured
	return view === 'split' && !isDesktop ? 'grid' : view
}

/**
 * Ordered, visible object tabs from CMS config. Mirrors the PHP normalization
 * (valid values only, de-duplicated) and falls back to units only when empty.
 * Portfolio mode offers a Properties tab in place of Plans.
 */
export function resolveObjectTabs (): CardType[] {
	const secondTab: CardType = isPortfolio() ? 'properties' : 'plans'
	const tabs: CardType[] = []
	for (const tab of getPageConfig().options.objectTabs ?? []) {
		if ((tab === 'units' || tab === secondTab) && !tabs.includes(tab)) tabs.push(tab)
	}
	return tabs.length ? tabs : ['units']
}

export function resolveDefaultObject (): CardType {
	const tabs = resolveObjectTabs()
	const configured = getPageConfig().options.defaultObject
	return configured && tabs.includes(configured) ? configured : tabs[0]
}
